import React, { useCallback, useState, useRef, createElement, Component } from 'react';
import { X, Upload, Wand2, AlertCircle, Sparkles, Check, Info, Clock, ArrowRight, Film, Image as ImageIcon, Zap, Eye, Download, Share2, Coins, Heart, Gift } from 'lucide-react';
// Types
interface DeepShiftModalProps {
  onClose: () => void;
  onComplete?: (result: RenderResult) => void;
  ritualId?: string | null;
}
interface RenderResult {
  id: string;
  url: string;
  type: 'image' | 'video' | 'gif';
  prompt: string;
  model: string;
  createdAt: Date;
  hdEnabled: boolean;
  preserveBackground: boolean;
  motionSmooth: boolean;
  creditCost: number;
}
interface ModelOption {
  id: string;
  name: string;
  label: string;
  isHot?: boolean;
  isBeta?: boolean;
  isPro?: boolean;
  description: string;
}
// Component
export const DeepShiftModal: React.FC<DeepShiftModalProps> = ({
  onClose,
  onComplete,
  ritualId
}) => {
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  // State for tabs
  const [activeTab, setActiveTab] = useState<'image-to-video' | 'video-extend' | 'text-to-video'>('image-to-video');
  // State for file upload
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // State for options
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState<string>('shifttransformer-v2');
  const [hdEnabled, setHdEnabled] = useState(false);
  const [preserveBackground, setPreserveBackground] = useState(true);
  const [motionSmooth, setMotionSmooth] = useState(true);
  // State for rendering
  const [isRendering, setIsRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderResult, setRenderResult] = useState<RenderResult | null>(null);
  const [renderEta, setRenderEta] = useState<string | null>(null);
  // State for credits
  const [userCredits, setUserCredits] = useState({
    desireGems: 120,
    loveTokens: 240
  });
  const [creditCost, setCreditCost] = useState(1);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  // Model options
  const modelOptions: ModelOption[] = [{
    id: 'shifttransformer-v1',
    name: 'ShiftTransformer v1.0',
    label: 'ShiftTransformer v1.0',
    description: 'Basic transformation model with good quality results.'
  }, {
    id: 'shifttransformer-v2',
    name: 'ShiftTransformer v2.0',
    label: 'ShiftTransformer v2.0',
    isHot: true,
    description: 'Improved transformation with better detail preservation.'
  }, {
    id: 'shifttransformer-v3.1',
    name: 'ShiftTransformer v3.1',
    label: 'ShiftTransformer v3.1',
    isBeta: true,
    description: 'Latest model with advanced scene understanding.'
  }, {
    id: 'hd-shift',
    name: 'HD Shift',
    label: 'HD Shift',
    isPro: true,
    description: 'Professional-grade transformations with maximum quality.'
  }];
  // Tab configuration
  const tabs = [{
    id: 'image-to-video',
    label: 'Image → Video',
    icon: <Film size={16} data-id="element-911" />
  }, {
    id: 'video-extend',
    label: 'Video Extend',
    icon: <ArrowRight size={16} data-id="element-912" />
  }, {
    id: 'text-to-video',
    label: 'Text → Video',
    icon: <div size={16} data-id="element-913" />
  }];
  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file type and size
    const validTypes = activeTab === 'text-to-video' ? [] // No file needed for text-to-video
    : activeTab === 'image-to-video' ? ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] : ['video/mp4', 'video/webm', 'image/gif'];
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (validTypes.length > 0 && !validTypes.includes(file.type)) {
      setUploadError(`Invalid file type. Please upload ${activeTab === 'image-to-video' ? 'an image' : 'a video'}.`);
      return;
    }
    if (file.size > maxSize) {
      setUploadError('File too large. Maximum size is 50MB.');
      return;
    }
    setUploadedFile(file);
    setUploadError(null);
    // Create preview
    const reader = new FileReader();
    reader.onload = e => {
      setFilePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      // Create a synthetic event to reuse validation logic
      const syntheticEvent = {
        target: {
          files: [file]
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileUpload(syntheticEvent);
    }
  }, [activeTab]);
  // Handle tab change
  const handleTabChange = (tabId: 'image-to-video' | 'video-extend' | 'text-to-video') => {
    setActiveTab(tabId);
    setUploadedFile(null);
    setFilePreview(null);
    setUploadError(null);
    setRenderResult(null);
  };
  // Handle model change
  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const model = e.target.value;
    setSelectedModel(model);
    // Adjust credit cost based on model
    if (model === 'hd-shift') {
      setCreditCost(3);
    } else if (model === 'shifttransformer-v3.1') {
      setCreditCost(2);
    } else {
      setCreditCost(1);
    }
  };
  // Handle HD toggle
  const handleHdToggle = () => {
    const newHdEnabled = !hdEnabled;
    setHdEnabled(newHdEnabled);
    // Adjust credit cost
    if (newHdEnabled) {
      setCreditCost(prev => prev + 1);
    } else {
      setCreditCost(prev => Math.max(1, prev - 1));
    }
  };
  // Handle render
  const handleRender = () => {
    // Check if user has enough credits
    const hasEnoughCredits = userCredits.desireGems >= creditCost || userCredits.loveTokens >= creditCost * 10;
    if (!hasEnoughCredits) {
      setInsufficientCredits(true);
      return;
    }
    // Check if we have required inputs
    if (activeTab !== 'text-to-video' && !uploadedFile) {
      setUploadError('Please upload a file to continue.');
      return;
    }
    if (!prompt && activeTab !== 'video-extend') {
      if (promptRef.current) {
        promptRef.current.focus();
      }
      return;
    }
    // Start rendering
    setIsRendering(true);
    setRenderProgress(0);
    setRenderEta('Estimating...');
    // Mock rendering progress
    const interval = setInterval(() => {
      setRenderProgress(prev => {
        const newProgress = prev + Math.random() * 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          // Simulate render completion
          setTimeout(() => {
            const result: RenderResult = {
              id: `render-${Date.now()}`,
              url: 'https://placehold.co/600x400/black/white?text=AI+Generated+Content',
              type: activeTab === 'text-to-video' || activeTab === 'video-extend' ? 'video' : 'image',
              prompt,
              model: selectedModel,
              createdAt: new Date(),
              hdEnabled,
              preserveBackground,
              motionSmooth,
              creditCost
            };
            setRenderResult(result);
            setIsRendering(false);
            // Deduct credits
            setUserCredits(prev => ({
              ...prev,
              desireGems: prev.desireGems - creditCost
            }));
            // Call completion callback
            if (onComplete) {
              onComplete(result);
            }
          }, 500);
          return 100;
        }
        // Update ETA
        const remainingPercent = 100 - newProgress;
        const secondsRemaining = Math.round(remainingPercent / 10) * 30;
        setRenderEta(secondsRemaining <= 60 ? `${secondsRemaining} seconds remaining` : `${Math.round(secondsRemaining / 60)} minutes remaining`);
        return newProgress;
      });
    }, 1000);
    // Cleanup on unmount
    return () => clearInterval(interval);
  };
  // Open token store
  const handleOpenTokenStore = () => {
    // Dispatch event to open token store
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'token_wallet'
      }
    });
    document.dispatchEvent(event);
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg animate-in fade-in duration-300" data-id="element-914">
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden animate-in zoom-in-90 duration-300 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800" data-id="element-915">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between" data-id="element-916">
          <div className="flex items-center gap-2" data-id="element-917">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-500/20 text-pink-500" data-id="element-918">
              <Wand2 size={18} data-id="element-919" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-920">
              Deep Shift
            </h2>
            <span className="px-2 py-0.5 bg-pink-500/20 text-pink-500 text-xs rounded-full" data-id="element-921">
              AI Powered
            </span>
          </div>
          <div className="flex items-center" data-id="element-922">
            {activeTab === 'image-to-video' && <div className="hidden md:block mr-4 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-923">
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-924">
                  <span className="text-pink-500 dark:text-pink-400 font-medium" data-id="element-925">
                    Image → Video:
                  </span>{' '}
                  Transform still photos into mesmerizing animations with
                  realistic motion
                </p>
              </div>}
            {activeTab === 'video-extend' && <div className="hidden md:block mr-4 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-926">
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-927">
                  <span className="text-pink-500 dark:text-pink-400 font-medium" data-id="element-928">
                    Video Extend:
                  </span>{' '}
                  Seamlessly continue your videos with AI-generated content that
                  matches the style
                </p>
              </div>}
            {activeTab === 'text-to-video' && <div className="hidden md:block mr-4 px-3 py-1.5 bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-929">
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-930">
                  <span className="text-pink-500 dark:text-pink-400 font-medium" data-id="element-931">
                    Text → Video:
                  </span>{' '}
                  Create custom videos from text descriptions with stunning
                  visual quality
                </p>
              </div>}
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors" onClick={onClose} data-id="element-932">
              <X size={18} className="text-gray-500 dark:text-gray-400" data-id="element-933" />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="px-6 pt-4 pb-2 border-b border-gray-200 dark:border-gray-800" data-id="element-934">
          <div className="flex space-x-1 overflow-x-auto scrollbar-hide" data-id="element-935">
            {tabs.map(tab => <button key={tab.id} className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50'}`} onClick={() => handleTabChange(tab.id as any)} data-id="element-936">
                {tab.icon}
                {tab.label}
              </button>)}
          </div>
        </div>
        <div className="flex flex-col md:flex-row h-[calc(90vh-8rem)] overflow-hidden" data-id="element-937">
          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-gray-100 dark:scrollbar-track-gray-900" data-id="element-938">
            {/* Upload section */}
            {activeTab !== 'text-to-video' && <div className="mb-6" data-id="element-939">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-940">
                  {activeTab === 'image-to-video' ? 'Upload Image' : 'Upload Video'}
                </h3>
                <div className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center transition-colors ${uploadError ? 'border-red-500/50 bg-red-500/5' : filePreview ? 'border-green-500/50 bg-green-500/5' : 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-600'}`} onDragOver={handleDragOver} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} data-id="element-941">
                  <input type="file" ref={fileInputRef} className="hidden" accept={activeTab === 'image-to-video' ? 'image/jpeg,image/png,image/gif,image/webp' : 'video/mp4,video/webm,image/gif'} onChange={handleFileUpload} data-id="element-942" />
                  {filePreview ? <div className="relative w-full max-w-md" data-id="element-943">
                      {activeTab === 'image-to-video' ? <img src={filePreview} alt="Preview" className="w-full h-auto max-h-64 rounded-lg object-contain" data-id="element-944" /> : <video src={filePreview} className="w-full h-auto max-h-64 rounded-lg" controls data-id="element-945" />}
                      <button className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white" onClick={e => {
                  e.stopPropagation();
                  setUploadedFile(null);
                  setFilePreview(null);
                }} data-id="element-946">
                        <X size={16} data-id="element-947" />
                      </button>
                    </div> : <>
                      <Upload size={36} className="text-gray-400 dark:text-gray-500 mb-2" data-id="element-948" />
                      <p className="text-gray-600 dark:text-gray-400 text-center mb-1" data-id="element-949">
                        {activeTab === 'image-to-video' ? 'Drag & drop an image, or click to browse' : 'Drag & drop a video, or click to browse'}
                      </p>
                      <p className="text-gray-500 text-xs text-center" data-id="element-950">
                        Supports{' '}
                        {activeTab === 'image-to-video' ? 'JPG, PNG, GIF, WebP' : 'MP4, WebM, GIF'}{' '}
                        up to 50MB
                      </p>
                    </>}
                </div>
                {uploadError && <div className="mt-2 px-3 py-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-600 dark:text-red-400" data-id="element-951">
                    <AlertCircle size={16} data-id="element-952" />
                    <span data-id="element-953">{uploadError}</span>
                  </div>}
              </div>}
            {/* Key frame preview */}
            {activeTab === 'image-to-video' && filePreview && <div className="mb-6" data-id="element-954">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-955">
                  Key Frame Preview
                </h3>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700" data-id="element-956">
                  <div className="flex flex-wrap gap-3" data-id="element-957">
                    {[...Array(4)].map((_, i) => <div key={i} className="w-24 h-24 rounded-lg bg-gray-200 dark:bg-gray-700 overflow-hidden flex items-center justify-center" data-id="element-958">
                        <img src={filePreview} alt={`Frame ${i + 1}`} className="w-full h-full object-cover opacity-70" data-id="element-959" />
                      </div>)}
                  </div>
                </div>
              </div>}
            {/* Prompt */}
            <div className="mb-6" data-id="element-960">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-961">
                Prompt
              </h3>
              <div className="relative" data-id="element-962">
                <textarea ref={promptRef} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={activeTab === 'text-to-video' ? 'Describe the video you want to create...' : 'Describe your transformation...'} className="w-full h-32 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent" maxLength={800} data-id="element-963" />
                <div className="absolute bottom-2 right-2 text-xs text-gray-500" data-id="element-964">
                  {prompt.length}/800
                </div>
              </div>
              {activeTab === 'text-to-video' && <div className="mt-2 px-3 py-2 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400" data-id="element-965">
                  <Info size={16} data-id="element-966" />
                  <span data-id="element-967">
                    Be detailed in your prompt for best results. Include scene
                    details, actions, and style.
                  </span>
                </div>}
            </div>
            {/* Model selector */}
            <div className="mb-6" data-id="element-968">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-969">
                Model
              </h3>
              <select value={selectedModel} onChange={handleModelChange} className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent appearance-none" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }} data-id="element-970">
                {modelOptions.map(model => <option key={model.id} value={model.id} disabled={model.isPro} data-id="element-971">
                    {model.label}
                    {model.isHot ? ' (HOT)' : ''}
                    {model.isBeta ? ' (BETA)' : ''}
                    {model.isPro ? ' (PRO)' : ''}
                  </option>)}
              </select>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-972">
                {modelOptions.map(model => model.id === selectedModel && <div key={`info-${model.id}`} className="col-span-full px-3 py-2 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center gap-2 text-sm" data-id="element-973">
                        {model.isHot ? <Sparkles size={16} className="text-amber-500 dark:text-amber-400" data-id="element-974" /> : model.isBeta ? <Sparkles size={16} className="text-blue-500 dark:text-blue-400" data-id="element-975" /> : model.isPro ? <Zap size={16} className="text-purple-500 dark:text-purple-400" data-id="element-976" /> : <Check size={16} className="text-green-500 dark:text-green-400" data-id="element-977" />}
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-978">
                          {model.description}
                        </span>
                      </div>)}
              </div>
            </div>
            {/* Toggles */}
            <div className="mb-6" data-id="element-979">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-980">
                Options
              </h3>
              <div className="space-y-3" data-id="element-981">
                <div className="flex items-center justify-between" data-id="element-982">
                  <div className="flex items-center gap-2" data-id="element-983">
                    <Zap size={18} className="text-purple-500 dark:text-purple-400" data-id="element-984" />
                    <span className="text-gray-900 dark:text-white" data-id="element-985">
                      HD Render
                    </span>
                    {selectedModel === 'hd-shift' && <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 text-xs rounded-full" data-id="element-986">
                        PRO
                      </span>}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-987">
                    <input type="checkbox" checked={hdEnabled} onChange={handleHdToggle} className="sr-only peer" disabled={selectedModel === 'hd-shift'} data-id="element-988" />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-pink-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500" data-id="element-989"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between" data-id="element-990">
                  <div className="flex items-center gap-2" data-id="element-991">
                    <Eye size={18} className="text-blue-500 dark:text-blue-400" data-id="element-992" />
                    <span className="text-gray-900 dark:text-white" data-id="element-993">
                      Preserve background
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-994">
                    <input type="checkbox" checked={preserveBackground} onChange={() => setPreserveBackground(!preserveBackground)} className="sr-only peer" data-id="element-995" />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-pink-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500" data-id="element-996"></div>
                  </label>
                </div>
                {(activeTab === 'image-to-video' || activeTab === 'text-to-video') && <div className="flex items-center justify-between" data-id="element-997">
                    <div className="flex items-center gap-2" data-id="element-998">
                      <Film size={18} className="text-green-500 dark:text-green-400" data-id="element-999" />
                      <span className="text-gray-900 dark:text-white" data-id="element-1000">
                        Motion smooth
                      </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-1001">
                      <input type="checkbox" checked={motionSmooth} onChange={() => setMotionSmooth(!motionSmooth)} className="sr-only peer" data-id="element-1002" />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-pink-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500" data-id="element-1003"></div>
                    </label>
                  </div>}
              </div>
            </div>
          </div>
          {/* Right sidebar - Credits and rendering */}
          <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-800 flex flex-col" data-id="element-1004">
            {/* Credit panel */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1005">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-1006">
                Credits
              </h3>
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-1007">
                <div className="flex items-center justify-between mb-2" data-id="element-1008">
                  <span className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-1009">
                    Cost per render:
                  </span>
                  <span className="text-gray-900 dark:text-white font-medium" data-id="element-1010">
                    {creditCost} credit{creditCost > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-4" data-id="element-1011">
                  <span className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-1012">
                    Your balance:
                  </span>
                  <div className="flex items-center gap-3" data-id="element-1013">
                    <div className="flex items-center gap-1" data-id="element-1014">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500" data-id="element-1015"></div>
                      <span className="text-gray-900 dark:text-white" data-id="element-1016">
                        {userCredits.desireGems}
                      </span>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-1017">
                      <div className="w-4 h-4 rounded-full bg-pink-500 flex items-center justify-center" data-id="element-1018">
                        <Heart size={10} className="text-white" data-id="element-1019" />
                      </div>
                      <span className="text-gray-900 dark:text-white" data-id="element-1020">
                        {userCredits.loveTokens}
                      </span>
                    </div>
                  </div>
                </div>
                <button className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-2" onClick={handleOpenTokenStore} data-id="element-1021">
                  <Coins size={16} data-id="element-1022" />
                  <span data-id="element-1023">Get more credits</span>
                </button>
              </div>
              {insufficientCredits && <div className="mb-4 px-3 py-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-600 dark:text-red-400" data-id="element-1024">
                  <AlertCircle size={16} data-id="element-1025" />
                  <span data-id="element-1026">Not enough credits. Please top up.</span>
                </div>}
              <button className={`w-full py-3 px-4 rounded-lg text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${isRendering ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700'}`} onClick={handleRender} disabled={isRendering} data-id="element-1027">
                {isRendering ? <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" data-id="element-1028"></div>
                    <span data-id="element-1029">Processing...</span>
                  </> : <>
                    <Wand2 size={16} data-id="element-1030" />
                    <span data-id="element-1031">
                      Create • {creditCost} credit{creditCost > 1 ? 's' : ''}
                    </span>
                  </>}
              </button>
            </div>
            {/* Render progress */}
            {isRendering && <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1032">
                <div className="flex items-center justify-between mb-2" data-id="element-1033">
                  <h4 className="text-gray-900 dark:text-white font-medium" data-id="element-1034">
                    Processing
                  </h4>
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-sm" data-id="element-1035">
                    <Clock size={14} data-id="element-1036" />
                    <span data-id="element-1037">{renderEta}</span>
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden mb-2" data-id="element-1038">
                  <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" style={{
                width: `${renderProgress}%`
              }} data-id="element-1039"></div>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-1040">
                  Creating your{' '}
                  {activeTab === 'text-to-video' || activeTab === 'video-extend' ? 'video' : 'transformation'}
                  ...
                </p>
              </div>}
            {/* Result preview */}
            {renderResult && <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1041">
                <h4 className="text-gray-900 dark:text-white font-medium mb-3" data-id="element-1042">
                  Result
                </h4>
                <div className="rounded-lg overflow-hidden mb-3" data-id="element-1043">
                  {renderResult.type === 'video' ? <video src={renderResult.url} className="w-full h-auto" controls autoPlay loop data-id="element-1044" /> : <img src={renderResult.url} alt="Result" className="w-full h-auto" data-id="element-1045" />}
                </div>
                <div className="flex gap-2" data-id="element-1046">
                  <button className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-1.5" onClick={() => {
                // Download logic
                const a = document.createElement('a');
                a.href = renderResult.url;
                a.download = `deepshift-${Date.now()}.${renderResult.type === 'video' ? 'mp4' : 'jpg'}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }} data-id="element-1047">
                    <Download size={14} data-id="element-1048" />
                    <span data-id="element-1049">Download</span>
                  </button>
                  <button className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-1.5" onClick={() => {
                // Share logic
                // This would typically open a share dialog
                alert('Share functionality would open here');
              }} data-id="element-1050">
                    <Share2 size={14} data-id="element-1051" />
                    <span data-id="element-1052">Share</span>
                  </button>
                </div>
              </div>}
            {/* Smart nudges */}
            <div className="p-6 flex-1" data-id="element-1053">
              <h4 className="text-gray-900 dark:text-white font-medium mb-3" data-id="element-1054">
                Tips
              </h4>
              <div className="space-y-3" data-id="element-1055">
                <div className="p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg flex items-start gap-2 text-sm" data-id="element-1056">
                  <Info size={16} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1057" />
                  <div data-id="element-1058">
                    <p className="text-blue-700 dark:text-blue-300 font-medium mb-1" data-id="element-1059">
                      Better results
                    </p>
                    <p className="text-gray-600 dark:text-gray-400" data-id="element-1060">
                      Use detailed prompts with specific style descriptions for
                      best results.
                    </p>
                  </div>
                </div>
                {userCredits.desireGems < 5 && <div className="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg flex items-start gap-2 text-sm" data-id="element-1061">
                    <AlertCircle size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" data-id="element-1062" />
                    <div data-id="element-1063">
                      <p className="text-amber-700 dark:text-amber-300 font-medium mb-1" data-id="element-1064">
                        Top up to continue
                      </p>
                      <p className="text-gray-600 dark:text-gray-400" data-id="element-1065">
                        Your credits are running low. Get more to continue
                        creating.
                      </p>
                    </div>
                  </div>}
                {userCredits.desireGems >= 3 && userCredits.desireGems <= 5 && <div className="p-3 bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 rounded-lg flex items-start gap-2 text-sm" data-id="element-1066">
                    <Gift size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" data-id="element-1067" />
                    <div data-id="element-1068">
                      <p className="text-purple-700 dark:text-purple-300 font-medium mb-1" data-id="element-1069">
                        2 more actions to unlock
                      </p>
                      <p className="text-gray-600 dark:text-gray-400" data-id="element-1070">
                        Complete 2 more renders to unlock your Weekly Premium
                        Lootbox.
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};